import { DefaultButton, Dialog, DialogType, getTheme, PrimaryButton, Stack } from '@fluentui/react';
import React, { useState } from 'react';
import IvicosStrings from 'kits/language/stringKit';

export interface IOnCallModalProps {
    isOpen: boolean;
    onConfirm: (durationMinutes: number) => void;
    onCancel: () => void;
    onDismiss: () => void;
}

const DURATIONS = [5, 10, 15, 30, 45, 60] as const;

function getDurationLabel(minutes: number): string {
    switch (minutes) {
        case 5:
            return '5 min';
        case 10:
            return '10 min';
        case 15:
            return IvicosStrings.focusTime15Min;
        case 30:
            return IvicosStrings.focusTime30Min;
        case 45:
            return IvicosStrings.focusTime45Min;
        case 60:
            return IvicosStrings.focusTime1H;
        default:
            return `${minutes} min`;
    }
}

function DurationButton({
    minutes,
    selected,
    theme,
    onSelect
}: {
    minutes: number;
    selected: boolean;
    theme: ReturnType<typeof getTheme>;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            style={{
                padding: '10px 16px',
                borderRadius: 6,
                border: '1px solid ' + (selected ? theme.palette.themePrimary : theme.palette.neutralLight),
                backgroundColor: selected ? theme.palette.themePrimary : theme.palette.neutralLighter,
                color: selected ? theme.palette.white : theme.palette.black,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                minWidth: 72
            }}
        >
            {getDurationLabel(minutes)}
        </button>
    );
}

/**
 * Modal shown when user clicks "On call" status.
 * Title "On call", duration preset buttons in three rows (5–30 min, 45 min, 1 h). Confirm sets status + timer, Cancel sets available.
 */
const OnCallModal: React.FC<IOnCallModalProps> = ({ isOpen, onConfirm, onCancel, onDismiss }) => {
    const theme = getTheme();
    const [selectedDurationMinutes, setSelectedDurationMinutes] = useState<number>(15);

    const handleConfirm = () => {
        onConfirm(selectedDurationMinutes);
    };

    const handleDismiss = () => {
        onCancel();
        onDismiss();
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={handleDismiss}
            maxWidth="500px"
            minWidth="500px"
            dialogContentProps={{
                type: DialogType.close,
                title: IvicosStrings.onCallModalTitle,
                closeButtonAriaLabel: IvicosStrings.onCallModalCloseAriaLabel
            }}
            modalProps={{ isBlocking: true }}
            styles={{ main: { borderRadius: 10, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' } }}
        >
            <Stack tokens={{ childrenGap: 24 }}>
                <Stack
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 12
                    }}
                >
                    {DURATIONS.map((min) => (
                        <DurationButton
                            key={min}
                            minutes={min}
                            selected={selectedDurationMinutes === min}
                            theme={theme}
                            onSelect={() => setSelectedDurationMinutes(min)}
                        />
                    ))}
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end" style={{ marginTop: 50 }}>
                    <DefaultButton text={IvicosStrings.onCallModalCancel} onClick={handleDismiss} />
                    <PrimaryButton text={IvicosStrings.onCallModalConfirm} onClick={handleConfirm} />
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default OnCallModal;
